import { createContext, FC, ReactNode, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

/**
 * Visibilità GLOBALE di moduli/pagine/tab (feature-flag runtime).
 * Fonte: GET /module-visibility (config singleton, editabile da mongo-express o PUT).
 * Semantica FLAT: flags["fe:<key>"] / flags["fe:tab:<key>"] → false = nascosto.
 * Flag ASSENTE o !== false = visibile (fail-open: un errore di rete non nasconde nulla).
 * Catalogo chiavi: module-visibility/seed-default.json (root repo).
 */
export interface IModuleVisibilityConfig {
  version: number;
  flags: Record<string, boolean>;
}

interface IModuleVisibilityContext {
  flags: Record<string, boolean>;
  isLoaded: boolean;
}

const ModuleVisibilityContext = createContext<IModuleVisibilityContext>({
  flags: {},
  isLoaded: false,
});

export const ModuleVisibilityProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { data, isSuccess } = useQuery({
    queryKey: ["module-visibility"],
    queryFn: async () => {
      const res = await axios.get<IModuleVisibilityConfig>(
        `${BASE_URL}/module-visibility`
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });

  const value = useMemo<IModuleVisibilityContext>(
    () => ({ flags: data?.flags ?? {}, isLoaded: isSuccess }),
    [data, isSuccess]
  );

  return (
    <ModuleVisibilityContext.Provider value={value}>
      {children}
    </ModuleVisibilityContext.Provider>
  );
};

export const useModuleVisibilityContext = () =>
  useContext(ModuleVisibilityContext);
